import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, KeyRound, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * v2.6.9 batch BD · Forgot Password dialog
 * Two-step flow:
 *  1) email → POST /api/auth/forgot-password  (always 200 generic)
 *  2) email + 6-digit code + new password → POST /api/auth/reset-password
 *
 * Works for ANY account email (driver / supervisor / dispatcher / rider).
 * Anti-enumeration: the UI shows the same "code sent" confirmation regardless
 * of whether the email is on file — matching backend behavior.
 */
export default function ForgotPasswordDialog({ open, onOpenChange, defaultEmail = "" }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(defaultEmail);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setStep(1); setCode(""); setNewPassword(""); setConfirmPassword(""); setDone(false);
  };
  const close = () => { reset(); onOpenChange?.(false); };

  const sendCode = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      toast.success("If that email is on file, a 6-digit code was sent.");
      setStep(2);
    } catch (err) {
      // Generic — surface a "check your spelling" if pydantic rejected it
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Could not send reset code");
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      return toast.error("Enter the 6-digit code from the email");
    }
    if (newPassword.length < 8) {
      return toast.error("New password must be at least 8 characters");
    }
    if (newPassword !== confirmPassword) {
      return toast.error("Passwords don't match");
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email: email.trim(), code, new_password: newPassword });
      setDone(true);
      toast.success("Password updated — please sign in.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid or expired code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); else onOpenChange?.(true); }}>
      <DialogContent
        className="bg-zinc-950 border-amber-400/40 text-zinc-100 max-w-md"
        data-testid="forgot-password-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400">
            <KeyRound className="h-5 w-5"/>Reset Your Password
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">
            {step === 1 && !done && "We'll email a 6-digit code to your account email."}
            {step === 2 && !done && "Enter the code from your email plus a new password (min 8 chars)."}
            {done && "All set!"}
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="text-center py-6 space-y-3" data-testid="forgot-pw-done">
            <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto"/>
            <div className="text-lg font-bold text-green-400">Password Updated</div>
            <p className="text-sm text-zinc-400">Sign in with your new password.</p>
            <Button data-testid="forgot-pw-close" onClick={close} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">
              CONTINUE TO SIGN IN
            </Button>
          </div>
        ) : step === 1 ? (
          <form onSubmit={sendCode} className="space-y-3" data-testid="forgot-pw-step-1">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Account Email</Label>
              <div className="relative mt-1">
                <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"/>
                <Input
                  data-testid="forgot-pw-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                  className="bg-zinc-900 border-zinc-700 h-11 pl-9"
                />
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
              <Button type="button" variant="outline" onClick={close} disabled={busy}
                className="bg-zinc-900 border-zinc-700">Cancel</Button>
              <Button
                data-testid="forgot-pw-send-btn"
                type="submit"
                disabled={busy || !email.trim()}
                className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "SEND CODE"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <form onSubmit={submitReset} className="space-y-3" data-testid="forgot-pw-step-2">
            <div className="text-[11px] text-zinc-500">Code was sent to <b className="text-zinc-300">{email}</b>. Check your inbox + spam.</div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">6-Digit Code</Label>
              <Input
                data-testid="forgot-pw-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                inputMode="numeric"
                maxLength={6}
                required
                autoFocus
                className="bg-zinc-900 border-zinc-700 h-12 text-center text-2xl meter-display tracking-[0.4em] mt-1"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">New Password (min 8 chars)</Label>
              <Input
                data-testid="forgot-pw-new"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New password"
                minLength={8}
                required
                className="bg-zinc-900 border-zinc-700 h-11 mt-1"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Confirm Password</Label>
              <Input
                data-testid="forgot-pw-confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                minLength={8}
                required
                className="bg-zinc-900 border-zinc-700 h-11 mt-1"
              />
            </div>
            <button
              type="button"
              data-testid="forgot-pw-resend"
              onClick={() => { setStep(1); setCode(""); }}
              className="text-[11px] text-zinc-400 hover:text-amber-400 underline-offset-2 hover:underline"
            >
              Didn't get a code? Send again
            </button>
            <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
              <Button type="button" variant="outline" onClick={close} disabled={busy}
                className="bg-zinc-900 border-zinc-700">Cancel</Button>
              <Button
                data-testid="forgot-pw-submit-btn"
                type="submit"
                disabled={busy || code.length !== 6 || newPassword.length < 8}
                className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "RESET PASSWORD"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
